import { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import "./GooeyNav.css";

const GooeyNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const filterRef = useRef(null);
  const textRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const navItems = [
    { path: '/', label: 'Professional' },
    { path: '/blogs', label: 'Thoughts/Blogs' },
    { path: '/personal', label: 'Personal' },
  ];

  const [activeIndex, setActiveIndex] = useState(() => {
    const currentPath = location.pathname;
    
    // Handle blog post routes - if path starts with /blog/, consider it as blogs tab
    if (currentPath.startsWith('/blog/')) {
      return 1; // Blogs tab index
    }
    
    const index = navItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  });

  const noise = (n = 1) => n / 2 - Math.random() * n;

  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getXY = (distance, pointIndex, totalPoints) => {
    const angle = ((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180);
    return [distance * Math.cos(angle), distance * Math.sin(angle)];
  };

  const createParticles = (element) => {
    const particleCount = 15;
    const particleDistances = [60, 8];
    const particleR = 80;
    const animationTime = 600;
    const timeVariance = 300;

    element.style.setProperty("--time", `${animationTime * 2 + timeVariance}ms`);

    for (let i = 0; i < particleCount; i++) {
      const t = animationTime * 2 + noise(timeVariance * 2);
      const p = {
        start: getXY(particleDistances[0], particleCount - i, particleCount),
        end: getXY(particleDistances[1] + noise(5), particleCount - i, particleCount),
        time: t,
        scale: 1 + noise(0.2),
        rotate: noise(particleR / 10) > 0 ? (noise(particleR / 10) + particleR / 20) * 10 : (noise(particleR / 10) - particleR / 20) * 10,
      };

      setTimeout(() => {
        const particle = document.createElement("span");
        const point = document.createElement("span");
        
        particle.classList.add("particle");
        particle.style.setProperty("--start-x", `${p.start[0]}px`);
        particle.style.setProperty("--start-y", `${p.start[1]}px`);
        particle.style.setProperty("--end-x", `${p.end[0]}px`);
        particle.style.setProperty("--end-y", `${p.end[1]}px`);
        particle.style.setProperty("--time", `${p.time}ms`);
        particle.style.setProperty("--scale", `${p.scale}`);
        particle.style.setProperty("--rotate", `${p.rotate}deg`);

        point.classList.add("point");
        particle.appendChild(point);
        element.appendChild(particle);
        
        requestAnimationFrame(() => {
          element.classList.add("active");
        });

        setTimeout(() => {
          try {
            element.removeChild(particle);
          } catch {
            // Do nothing
          }
        }, t);
      }, 30);
    }
  };

  const updateEffectPosition = (element) => {
    if (!containerRef.current || !filterRef.current || !textRef.current) return;
    
    // Use requestAnimationFrame to ensure smooth positioning
    requestAnimationFrame(() => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const pos = element.getBoundingClientRect();

      const styles = {
        left: `${pos.x - containerRect.x}px`,
        top: `${pos.y - containerRect.y}px`,
        width: `${pos.width}px`,
        height: `${pos.height}px`,
      };
      
      // Apply styles without transition first to prevent sliding
      filterRef.current.style.transition = 'none';
      textRef.current.style.transition = 'none';
      
      Object.assign(filterRef.current.style, styles);
      Object.assign(textRef.current.style, styles);
      textRef.current.innerText = element.innerText;
      
      // Re-enable transitions after positioning
      requestAnimationFrame(() => {
        filterRef.current.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        textRef.current.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      });
    });
  };

  const handleClick = (item, index) => {
    // Update active index immediately to prevent sliding
    setActiveIndex(index);
    
    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
  };

  // Update effect position on mount and route change
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Handle blog post routes - if path starts with /blog/, consider it as blogs tab
    let index;
    if (currentPath.startsWith('/blog/')) {
      index = 1; // Blogs tab index
    } else {
      index = navItems.findIndex(item => item.path === currentPath);
    }
    
    if (index >= 0) {
      setActiveIndex(index);
      
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        if (navRef.current) {
          const activeLi = navRef.current.querySelectorAll("li")[index];
          if (activeLi) {
            updateEffectPosition(activeLi);
            textRef.current?.classList.add("active");
            if (filterRef.current) {
              createParticles(filterRef.current);
            }
          }
        }
      }, 50);
    }
    // Intentionally only run on pathname change; createParticles/navItems are stable for this effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!navRef.current || !containerRef.current) return;
    const activeLi = navRef.current.querySelectorAll("li")[activeIndex];
    if (activeLi) {
      updateEffectPosition(activeLi);
      textRef.current?.classList.add("active");
    }

    const resizeObserver = new ResizeObserver(() => {
      const currentActiveLi = navRef.current?.querySelectorAll("li")[activeIndex];
      if (currentActiveLi) {
        updateEffectPosition(currentActiveLi);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [activeIndex]);

  // Render regular nav for mobile
  if (isMobile) {
    return (
      <div className="regular-nav-container">
        <nav className="regular-nav">
          <ul>
            {navItems.map((item, index) => (
              <li
                key={item.label}
                className={activeIndex === index ? "active" : ""}
                onClick={() => handleClick(item, index)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    );
  }

  // Render gooey nav for desktop
  return (
    <div className="gooey-nav-container" ref={containerRef}>
      <nav>
        <ul ref={navRef}>
          {navItems.map((item, index) => (
            <li
              key={item.label}
              className={activeIndex === index ? "active" : ""}
              onClick={() => handleClick(item, index)}
            >
              {item.label}
            </li>
          ))}
        </ul>
      </nav>
      <span className="effect filter" ref={filterRef} />
      <span className="effect text" ref={textRef} />
    </div>
  );
};

export default GooeyNav; 